/**
 * HTTP client for the LumaHMS API.
 *
 * - The short-lived access token lives only in memory (never localStorage).
 * - The refresh token is an httpOnly cookie; on 401 we refresh once (single flight) and retry.
 * - Errors are normalized into ApiError with the backend's { code, message, details } envelope.
 */

// VITE_API_BASE_URL (same name as PerfexData's other products); VITE_API_BASE kept as a fallback.
export const API_BASE: string = (import.meta.env.VITE_API_BASE_URL ?? import.meta.env.VITE_API_BASE ?? '/api/v1').replace(/\/$/, '')

type NetworkListener = (reachable: boolean) => void
const networkListeners = new Set<NetworkListener>()
/** Lets offline mode learn about connectivity from real API traffic. */
export function onNetworkChange(fn: NetworkListener) {
  networkListeners.add(fn)
  return () => networkListeners.delete(fn)
}

export class ApiError extends Error {
  status: number
  code: string
  details: unknown
  requestId?: string
  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message)
    this.status = status
    this.code = code
    this.details = details
    this.requestId = requestId
  }
  /** Field errors from a 422 validation response, keyed by field name. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    if (Array.isArray(this.details)) {
      for (const d of this.details as { loc?: string[]; msg?: string }[]) {
        const key = d.loc?.filter((x) => x !== 'body').join('.')
        if (key && d.msg) out[key] = d.msg.replace(/^Value error, /, '')
      }
    }
    return out
  }
}

type TokenListener = (token: string | null) => void

interface Channel {
  token: string | null
  refreshPath: string
  refreshing: Promise<string | null> | null
  listeners: Set<TokenListener>
}

const channels: Record<'staff' | 'portal', Channel> = {
  staff: { token: null, refreshPath: '/auth/refresh', refreshing: null, listeners: new Set() },
  portal: { token: null, refreshPath: '/portal/refresh', refreshing: null, listeners: new Set() },
}

let branchId: string | null = null

export function setBranch(id: string | null) {
  branchId = id
}

export function setToken(token: string | null, audience: 'staff' | 'portal' = 'staff') {
  const ch = channels[audience]
  ch.token = token
  ch.listeners.forEach((l) => l(token))
}

export function getToken(audience: 'staff' | 'portal' = 'staff') {
  return channels[audience].token
}

export function onTokenChange(fn: TokenListener, audience: 'staff' | 'portal' = 'staff') {
  channels[audience].listeners.add(fn)
  return () => channels[audience].listeners.delete(fn)
}

export async function refreshToken(audience: 'staff' | 'portal' = 'staff'): Promise<string | null> {
  const ch = channels[audience]
  if (!ch.refreshing) {
    ch.refreshing = (async () => {
      try {
        const res = await fetch(API_BASE + ch.refreshPath, {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-Requested-With': 'lumahms' },
        })
        if (!res.ok) {
          setToken(null, audience)
          return null
        }
        const body = await res.json()
        setToken(body.access_token, audience)
        return body.access_token as string
      } catch {
        return null
      } finally {
        setTimeout(() => (ch.refreshing = null), 0)
      }
    })()
  }
  return ch.refreshing
}

export interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | null | undefined | string[]>
  audience?: 'staff' | 'portal' | 'public'
  raw?: boolean
  signal?: AbortSignal
  headers?: Record<string, string>
}

function buildUrl(path: string, query?: RequestOptions['query']) {
  const url = new URL(API_BASE + path, window.location.origin)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue
      if (Array.isArray(v)) v.forEach((x) => url.searchParams.append(k, x))
      else url.searchParams.set(k, String(v))
    }
  }
  return url.pathname + url.search
}

async function toError(res: Response): Promise<ApiError> {
  let body: { error?: { code?: string; message?: string; details?: unknown }; request_id?: string } = {}
  try {
    body = await res.json()
  } catch {
    /* non-JSON error */
  }
  const e = body.error ?? {}
  const fallback =
    res.status === 403
      ? 'You do not have permission to do this.'
      : res.status >= 500
        ? 'The server had a problem. Please try again.'
        : 'Request failed.'
  return new ApiError(res.status, e.code ?? 'error', e.message ?? fallback, e.details, body.request_id)
}

export async function request<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const audience = opts.audience ?? 'staff'
  const doFetch = async () => {
    const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers }
    const isForm = opts.body instanceof FormData
    if (opts.body !== undefined && !isForm) headers['Content-Type'] = 'application/json'
    if (audience !== 'public') {
      const t = channels[audience].token
      if (t) headers.Authorization = `Bearer ${t}`
    }
    if (audience === 'staff' && branchId) headers['X-Branch-Id'] = branchId
    return fetch(buildUrl(path, opts.query), {
      method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
      headers,
      credentials: 'include',
      body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
      signal: opts.signal,
    })
  }
  let res: Response
  try {
    res = await doFetch()
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    networkListeners.forEach((l) => l(false))
    throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection and try again.')
  }
  networkListeners.forEach((l) => l(true))
  if (res.status === 401 && audience !== 'public' && !path.endsWith('/login')) {
    const t = await refreshToken(audience)
    if (t) res = await doFetch()
  }
  if (!res.ok) throw await toError(res)
  if (opts.raw) return res as unknown as T
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T,>(path: string, query?: RequestOptions['query'], audience?: RequestOptions['audience']) =>
    request<T>(path, { query, audience }),
  post: <T,>(path: string, body?: unknown, audience?: RequestOptions['audience']) =>
    request<T>(path, { method: 'POST', body: body ?? {}, audience }),
  put: <T,>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T,>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Downloads an authenticated file and hands it to the browser. */
export async function download(path: string, fallbackName = 'download') {
  const res = await request<Response>(path, { raw: true })
  const blob = await res.blob()
  const cd = res.headers.get('content-disposition') ?? ''
  const name = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? fallbackName
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 2000)
}

/** Opens an authenticated file (e.g. PDF/image) in a new tab. */
export async function openBlob(path: string) {
  const res = await request<Response>(path, { raw: true })
  const url = URL.createObjectURL(await res.blob())
  window.open(url, '_blank', 'noopener')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}
